import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../../../../context/CartContext";
import { logout } from "../../../../api/action/user";
import { getAllProducts } from "../../../../api/action/products";
import { Menu, MenuItem } from "../../../../components/ui/MenuPrimitives";
import { UserCircle } from "lucide-react";
import { useTemplateAuthState } from "../../../../template/TemplateAuthState";

const SunFlareTemplateHeader = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const nav = useNavigate();
  const dispatch = useDispatch();
  const { isLoggedIn: isAuthenticated } = useTemplateAuthState();
  const companyState = useSelector((state) => state.companyReducer.company);
  const companyPrimayId = companyState?.primaryId;
  const [categories, setCategories] = useState([]);
  const { total_items } = useCart();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null); // For avatar dropdown menu

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchProducts = async (retries = 3, delay = 1000) => {
      try {
        if (!companyPrimayId) return;
        const response = await getAllProducts(companyPrimayId)(dispatch);

        if (response?.length > 0) {
          const uniqueCategories = [
            ...new Set(response.map((product) => product.category.name)),
          ];
          setCategories(uniqueCategories);
        }
      } catch {
        if (retries > 0) {
          await new Promise((resolve) => setTimeout(resolve, delay));
          fetchProducts(retries - 1, delay);
        }
      }
    };

    fetchProducts();
  }, [companyPrimayId, dispatch]);

  const handleNavigation = (e, value) => {
    if (value === "Logout") {
      void dispatch(logout({ reason: "template-header-logout" })).finally(() => {
        nav("/sign-in", { replace: true });
      });
    } else {
      nav(value);
    }
  };

  const handleCart = () => {
    if (!isAuthenticated) {
      nav("/sign-in");
      return;
    }
    nav("/cart");
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const [isHeaderVisible, setIsHeaderVisible] = useState(true);

  useEffect(() => {
    const handleError = localStorage.getItem("handleError");
    if (handleError === "true") {
      setIsHeaderVisible(false);
    } else {
      setIsHeaderVisible(true);
    }

    const handleStorageChange = (event) => {
      if (event.key === "handleError") {
        setIsHeaderVisible(event.newValue !== "true");
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  if (!isHeaderVisible) {
    return null;
  }

  return (
    <div className="bg-white">
      <div className="border border-b-[#DDE2E4] px-4 py-2">
        <div className="flex justify-between">
          <div className="flex items-center">
            <div className="flex items-center space-x-2">
              <Link to="/" title="AVPS Technologies Inc.">
                <img
                  loading="lazy"
                  src={companyState?.logoLocation}
                  alt="Company Logo"
                  className="h-12"
                />
              </Link>
            </div>
          </div>

          <div className="ml-6 flex flex-1 gap-x-3">
            <div
              className="flex cursor-pointer select-none items-center gap-x-2 rounded-md border bg-[#B88E2F] py-2 px-4 text-white hover:bg-[#A67C2A]"
              onClick={toggleSidebar}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              <span className="text-sm font-medium">Categories</span>
            </div>

            <input
              type="text"
              className="w-full rounded-md border border-[#DDE2E4] px-3 py-2 text-sm"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>

          <div className="ml-2 flex">
            <div
              className="flex cursor-pointer items-center gap-x-1 rounded-md py-2 px-4 hover:bg-gray-100"
              onClick={() => nav("/orders")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-gray-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
                <path
                  fillRule="evenodd"
                  d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-sm font-medium">Orders</span>
            </div>

            <div
              className="flex cursor-pointer items-center gap-x-1 rounded-md py-2 px-4 hover:bg-gray-100"
              onClick={() => nav("/favorites")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-gray-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-sm font-medium">Favorites</span>
            </div>

            <div
              className="flex cursor-pointer items-center gap-x-1 rounded-md py-2 px-4 hover:bg-gray-100"
              onClick={handleCart}
            >
              <div className="relative">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-gray-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                </svg>
                <span className="absolute -top-2 -right-2 flex h-4 w-4 items-center justify-center rounded-full bg-[#B88E2F] p-2 text-xs text-white">
                  {total_items}
                </span>
              </div>
              <span className="text-sm font-medium">Cart</span>
            </div>

            {isAuthenticated ? (
              <div className="ml-2 flex items-center">
                <button
                  type="button"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-600 transition hover:bg-slate-100"
                  onClick={handleAvatarClick}
                  aria-label="Open profile menu"
                >
                  <UserCircle className="h-6 w-6" />
                </button>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem onClick={() => nav("/my-profile")}>Profile</MenuItem>
                  <MenuItem onClick={() => handleNavigation(null, "Logout")}>
                    Logout
                  </MenuItem>
                  {/* <MenuItem onClick={() => nav("/my-profile")}>Products</MenuItem> */}

                </Menu>
              </div>
            ) : (
              <div
                className="ml-2 flex cursor-pointer items-center gap-x-1 rounded-md border py-2 px-4 hover:bg-gray-100"
                onClick={() => nav("/sign-in")}
              >
                <span className="text-sm font-medium">Sign in</span>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex gap-x-8">
            {categories.slice(0, 8).map((category, index) => (
              <span
                key={index}
                className="cursor-pointer rounded-sm py-1 px-2 text-sm font-medium hover:bg-gray-100"
                onClick={() => nav(`/category/sf/${category}`)}
              >
                {category}
              </span>
            ))}
          </div>
        </div>
      </div>

      {isSidebarOpen && (
        <div className="fixed inset-0 z-50">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={toggleSidebar}
            aria-label="Close categories"
          />
          <aside className="relative h-full w-72 bg-white p-4 shadow-xl">
        <ul className="space-y-1">
          {categories.map((category, index) => (
            <li
              key={index}
            >
              <button
                type="button"
                className="w-full rounded px-3 py-2 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                onClick={() => {
                  nav(`/category/${category}`);
                  toggleSidebar();
                }}
              >
                {category}
              </button>
            </li>
          ))}
        </ul>
          </aside>
        </div>
      )}
    </div>
  );
};

export default SunFlareTemplateHeader;
